/**
 * nn_worker_client.js — Singleton client for the shared NN inference worker.
 *
 * Usage:
 *   import { loadModel, postInferRequest, onModelLoaded } from './nn_worker_client.js';
 *
 *   // Tell the worker to load a model (fire-and-forget; result via onModelLoaded callback).
 *   loadModel(faction, urls);
 *
 *   // Register a callback for when a model finishes loading (success or failure).
 *   onModelLoaded((faction, success, modelVersion) => { ... });
 *
 *   // Submit an inference request; callback fires with the result.
 *   postInferRequest(faction, snapshot, numSpawners, modelVersion, (result) => { ... });
 */

import { reportIntegrityEvent } from './integrity.js';

/** @type {Worker|null} */
let _worker = null;
let _requestId = 0;

/** @type {Map<number, Function>} requestId → callback */
const _inferCallbacks = new Map();

/** @type {Array<Function>} */
const _modelLoadedListeners = [];

/** @type {Set<number>} factions with a loadModel request awaiting a reply */
const _pendingLoads = new Set();

/**
 * @type {Map<number, string>} faction → URL signature of the in-flight or
 * loaded model. Dedupes repeat loadModel() calls: initializeAis() runs on
 * every level load, and re-fetching an already-loaded model would waste
 * bandwidth and stall inference.
 */
const _requestedModels = new Map();

function getWorker() {
    if (typeof Worker === 'undefined') {
        // No Worker API in test/headless environments — inference is skipped.
        return null;
    }
    if (!_worker) {
        _worker = new Worker(
            new URL('./nn_inference_worker.js', import.meta.url),
            { type: 'module' }
        );
        _worker.onmessage = (e) => {
            const msg = e.data;
            if (msg.type === 'modelLoaded') {
                _pendingLoads.delete(msg.faction);
                if (!msg.success) _requestedModels.delete(msg.faction); // allow retry
                for (const cb of _modelLoadedListeners) {
                    cb(msg.faction, msg.success, msg.modelVersion);
                }
            } else if (msg.type === 'inferResult') {
                const cb = _inferCallbacks.get(msg.requestId);
                if (cb) {
                    _inferCallbacks.delete(msg.requestId);
                    cb(msg);
                }
            } else if (msg.type === 'tfjsLoadFailed') {
                // Worker has no DOM/analytics; report from the page context.
                reportIntegrityEvent('tfjs',
                    'Pinned TF.js module failed to load — offline/blocked, or the pinned version no longer resolves. Neural AI falls back to heuristic.',
                    { url: msg.url, message: msg.message });
            }
        };
        _worker.onerror = (err) => {
            console.error('[NNWorkerClient] Worker error:', err.message);
            // If the worker script itself failed to load, no modelLoaded reply
            // will ever arrive — report failure for every outstanding load so
            // callers can mark those factions idle instead of waiting forever.
            for (const faction of _pendingLoads) {
                _requestedModels.delete(faction);
                for (const cb of _modelLoadedListeners) {
                    cb(faction, false, undefined);
                }
            }
            _pendingLoads.clear();
        };
    }
    return _worker;
}

/**
 * Register a listener called whenever any faction's model finishes loading.
 * @param {Function} cb - Called with (faction: number, success: boolean, modelVersion: string)
 */
function onModelLoaded(cb) {
    _modelLoadedListeners.push(cb);
}

/**
 * Tell the worker to load a model for a faction.
 * Result arrives via the onModelLoaded listener.
 * @param {number} faction
 * @param {string[]} urls - Tried in order; first success wins.
 */
function loadModel(faction, urls) {
    const w = getWorker();
    if (!w) return;
    const signature = urls.join('\n');
    if (_requestedModels.get(faction) === signature) return; // already loaded or in flight
    _requestedModels.set(faction, signature);
    // Resolve relative paths against the page origin before sending to the worker,
    // since the worker resolves URLs relative to its own script location (src/js/),
    // not the web root where index.html and the models/ directory live.
    const absoluteUrls = urls.map((url) => new URL(url, document.baseURI).href);
    _pendingLoads.add(faction);
    w.postMessage({ type: 'loadModel', faction, urls: absoluteUrls });
}

/**
 * Submit an inference request. The callback fires asynchronously with the result.
 *
 * @param {number} faction
 * @param {{encoded: {data: Float32Array, shape: number[]}, paddedSpawnerFeatures: Float32Array|null}} snapshot
 * @param {number} numSpawners
 * @param {string} modelVersion
 * @param {Function} callback - Called with {policy, value, easyPickings, contestRisk, failed?}
 */
function postInferRequest(faction, snapshot, numSpawners, modelVersion, callback) {
    const w = getWorker();
    if (!w) return; // No Worker available (headless/test); leave _pendingResult null

    const requestId = ++_requestId;
    _inferCallbacks.set(requestId, callback);

    const gridData = snapshot.encoded.data;
    const gridShape = snapshot.encoded.shape;
    const spawnerData = snapshot.paddedSpawnerFeatures;

    const transferables = [gridData.buffer];
    if (spawnerData) transferables.push(spawnerData.buffer);

    w.postMessage(
        { type: 'infer', requestId, faction, gridData, gridShape, spawnerData, numSpawners, modelVersion },
        transferables
    );
}

export { loadModel, postInferRequest, onModelLoaded };
